import React from 'react';
import useFundNotes from '../../../../hooks/useFundNotes';
import { Note } from '../../../../sharedInterfaces/notes';
import { mainColors } from '../../../../styling/aifTheme';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import formatRule from '../../../../utilities/formatting';
import RaptorLoading from '../../../feedback/RaptorLoading';
import GridItem from '../../../layout/GridComponents/GridItem';
import CustomTable from '../../../tables/CustomTable';
import NotesTable from '../NotesPage/NotesTable/NotesTable.component';
import { Typography, useTheme } from '@mui/material';
import { UnfoldMore } from '@mui/icons-material';
import { NoteData } from '../../sharedComponents/notes/Notes';
import {
  FailRegulationBucket,
  isFailsMetaKey,
  isRuleData,
} from '../../../../api/fails/types';

export interface FundFailTableProps {
  title: string | JSX.Element;
  fundId: string;
  failsData: FailRegulationBucket;
  subject: string;
  section: string;
  isFetching: boolean;
}

export interface FundFailTableData {
  rule: string;
  ruleDescription: string;
  startFailDate: string;
  endFailDate: string;
  headerRow?: boolean;
}

const fundFailTableColumns: CustomColumn<FundFailTableData>[] = [
  {
    field: 'rule',
    title: 'Rule',
    cellStyle: {
      fontWeight: 700,
      color: mainColors.mainBlue,
      textAlign: 'center',
    },
    render: (rowData) => formatRule(rowData.rule),
    headerStyle: {
      textAlign: 'center',
    },
  },
  {
    field: 'ruleDescription',
    headerStyle: {
      textAlign: 'center',
    },
    title: 'Rule Description',
    render: (rowData) => rowData.ruleDescription,
    cellStyle: {
      fontWeight: 700,
      color: mainColors.mainBlue,
      textAlign: 'center',
    },
  },
  {
    field: 'startFailDate',
    title: 'Start Fail Date',
  },
  {
    field: 'endFailDate',
    title: 'End Fail Date',
  },
];

const generateFailsDataByFund = (
  regulationBucket: FailRegulationBucket | null | undefined,
): FundFailTableData[] => {
  if (!regulationBucket) return [];

  const updatedWithNewRows: FundFailTableData[] = [];

  for (const key of Object.keys(regulationBucket)) {
    if (isFailsMetaKey(key)) continue;

    const value = regulationBucket[key];
    if (!isRuleData(value)) continue;

    const [, ...failRows] = value.date_data; // skip the header row

    for (const [start, end] of failRows) {
      updatedWithNewRows.push({
        rule: value.rule,
        ruleDescription: value.rule_description,
        startFailDate: start,
        endFailDate: end,
      });
    }
  }

  return updatedWithNewRows.sort((a, b) => {
    const aNumber = parseInt(a.rule.replace(/\D/g, ''));
    const bNumber = parseInt(b.rule.replace(/\D/g, ''));

    if (aNumber === bNumber) {
      if (!a.headerRow && !b.headerRow) {
        return a.startFailDate.localeCompare(b.startFailDate);
      }
      return a.headerRow ? -1 : 1;
    }

    return aNumber - bNumber;
  });
};

export const NotesDropDown = ({
  rule,
  startDate,
  endDate,
  ruleDescription,
  fundId,
  subject,
  section,
}: {
  rule: string;
  startDate: string;
  ruleDescription: string;
  endDate: string;
  fundId: string;
  subject: string;
  section: string;
}) => {
  const { notes, loading } = useFundNotes({
    fundName: fundId,
    subject: `${section}_${subject}`,
    topic: rule,
    positionDate: startDate,
    shouldCall: true,
    startDate,
    endDate,
  });
  const theme = useTheme();

  const notesData: NoteData[] =
    notes.map((note: Note) => ({
      date: note.fund_note_timestamp,
      noteValue: note.fund_note_text,
      positionDate: note.fund_note_position_date,
      noteType: note.fund_note_text.includes('TABLE_NOTE:') ? 'table' : 'basic',
    })) || [];
  return notes ? (
    loading ? (
      <RaptorLoading setWidth="2.50rem" />
    ) : Object.keys(notes).length > 0 ? (
      <NotesTable
        // if we reverse, the earliest notes come at the top
        tableData={notesData.reverse()}
        // this is where we have to put in the correct rule title
        title={`${formatRule(rule)}: ${ruleDescription}`}
        showToolbar
      />
    ) : (
      <div style={{ padding: '0.50rem', display: 'flex' }}>
        <Typography
          variant="h5"
          style={{ fontWeight: 500, color: theme.palette.grey[600] }}
        >
          No notes saved for {formatRule(rule)} between {startDate} and{' '}
          {endDate}
        </Typography>
      </div>
    )
  ) : null;
};

export const FundFailTable: React.FC<FundFailTableProps> = ({
  title,
  failsData,
  fundId,
  subject,
  section,
  isFetching,
}) => {
  const dataForTable = generateFailsDataByFund(failsData);

  return (
    <GridItem xs={12} card>
      <CustomTable<FundFailTableData>
        emptyDataSourceMessage="No Fails Found for Specified Period."
        options={{
          paging: false,
          exportButton: true,
          detailPanelColumnStyle: {
            width: '0.5rem',
            minWidth: '0.5rem',
          },
        }}
        data={dataForTable}
        columns={fundFailTableColumns}
        showToolbar
        detailPanel={[
          (rowData: FundFailTableData) => {
            const icon = () => (!rowData.headerRow ? <UnfoldMore /> : <></>);
            const render = () => (
              <NotesDropDown
                subject={subject}
                section={section}
                rule={rowData.rule}
                ruleDescription={rowData.ruleDescription}
                startDate={rowData.startFailDate}
                endDate={rowData.endFailDate}
                fundId={fundId}
              />
            );
            return {
              render,
              disabled: rowData.headerRow,
              icon,
            };
          },
        ]}
        loading={isFetching}
        title={title}
      />
    </GridItem>
  );
};
